import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { MedicalRecordsQueueState } from '../hooks/useMedicalRecords';
import { MedicalRecordsDocumentAction } from './MedicalRecordsDocuments';
import { MedicalRecordsRetryAction } from './MedicalRecordsRetryAction';
import { MedicalRecordsSortControl } from './MedicalRecordsSortControl';
import type { MedicalRecordsSort } from '../data/medicalRecordsViews';

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  Queued: 'default',
  InProgress: 'info',
  Completed: 'success',
  Failed: 'error',
};

const ERROR_MESSAGES: Record<'denied' | 'unavailable' | 'general', string> = {
  denied: 'You do not have permission to view Medical Records requests for this center.',
  unavailable: 'The Medical Records queue is temporarily unavailable. Please try again shortly.',
  general: 'Unable to load the Medical Records queue. Please try again.',
};

/**
 * The current user's submitted Medical Records requests for this center. Rows reflect the
 * server's status field directly; the hook that feeds this component bounds polling so it stops
 * once nothing is Queued/InProgress.
 */
interface MedicalRecordsQueueTableProps {
  sort: MedicalRecordsSort;
  onSortChange: (sort: MedicalRecordsSort) => void;
  state: MedicalRecordsQueueState;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

const MedicalRecordsQueueHeader: React.FC<Pick<MedicalRecordsQueueTableProps, 'sort' | 'onSortChange'>> = ({
  sort, onSortChange,
}) => (
  <TableHead>
    <TableRow>
      <TableCell>Account</TableCell>
      <TableCell>Encounter / Visit</TableCell>
      <TableCell>Patient</TableCell>
      <TableCell>MRN</TableCell>
      {[['status', 'Status'], ['submitted', 'Submitted'], ['updated', 'Updated']].map(([field, label]) => (
        <TableCell key={field} sortDirection={sort.field === field && (sort.descending ? 'desc' : 'asc')}>
          <MedicalRecordsSortControl field={field} value={sort} onChange={onSortChange}>{label}</MedicalRecordsSortControl>
        </TableCell>
      ))}
      <TableCell>Actions</TableCell>
    </TableRow>
  </TableHead>
);

const MedicalRecordsQueueTable: React.FC<MedicalRecordsQueueTableProps> = ({
  state,
  onPreviousPage,
  onNextPage,
  sort, onSortChange,
}) => {
  if (state.status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress data-testid="medical-records-queue-loading" />
      </Box>
    );
  }

  if (state.status === 'error') {
    return (
      <Alert severity="error" data-testid="medical-records-queue-error">
        {ERROR_MESSAGES[state.kind]}
      </Alert>
    );
  }

  if (state.status === 'empty') {
    return (
      <Alert severity="info" data-testid="medical-records-queue-empty">
        No Medical Records requests yet.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        My Requests
      </Typography>
      <TableContainer data-testid="medical-records-queue-container" sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small" data-testid="medical-records-queue" sx={{ minWidth: 860 }}>
          <MedicalRecordsQueueHeader sort={sort} onSortChange={onSortChange} />
          <TableBody>
            {state.items.map((item) => (
              <TableRow key={item.requestId} data-testid="medical-records-queue-row">
                <TableCell>{item.accountNumber}</TableCell>
                <TableCell>{item.isDemo ? 'Synthetic example' : `${item.encounterId} / ${item.visitKey}`}</TableCell>
                <TableCell>{item.patientName}</TableCell>
                <TableCell>{item.mrn}</TableCell>
                <TableCell>
                  <Chip size="small" label={item.status} color={STATUS_COLOR[item.status] ?? 'default'} />
                  {item.isDemo && <Chip size="small" label="Synthetic demo" variant="outlined" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{new Date(item.submittedAt).toLocaleString()}</TableCell>
                <TableCell>{new Date(item.updatedAt).toLocaleString()}</TableCell>
                <TableCell><MedicalRecordsDocumentAction item={item} /><MedicalRecordsRetryAction item={item} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {(state.hasPrevious || state.hasNext) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
            <Tooltip title="Previous requests">
              <span>
                <IconButton aria-label="Previous requests" disabled={!state.hasPrevious} onClick={onPreviousPage}>
                  <ChevronLeftIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next requests">
              <span>
                <IconButton aria-label="Next requests" disabled={!state.hasNext} onClick={onNextPage}>
                  <ChevronRightIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </TableContainer>
    </Box>
  );
};

export default MedicalRecordsQueueTable;
